import React, { useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { FaCode, FaPlug, FaProjectDiagram } from "react-icons/fa";
import { Link, useSearchParams } from "react-router-dom";
import ProjectCard from "./ProjectCards";
import Particle from "../Particle";
import projectData, {
  normalizeProjectFocus,
  projectFocusOptions,
} from "../../data/projectData";
import usePageMeta from "../../hooks/usePageMeta";

const evaluationGuides = [
  {
    Icon: FaProjectDiagram,
    label: "Workflow clarity",
    text: "Complex steps turned into usable screens.",
  },
  {
    Icon: FaCode,
    label: "Frontend quality",
    text: "Hierarchy, states, and flows that feel deliberate.",
  },
  {
    Icon: FaPlug,
    label: "Selective integrations",
    text: "Connected workflows that help the product.",
  },
];

function Projects() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedFocus = searchParams.get("focus") || "all";
  const normalizedRequestedFocus = normalizeProjectFocus(requestedFocus);
  const activeFocus = projectFocusOptions.some((item) => item.id === requestedFocus)
    ? requestedFocus
    : projectFocusOptions.some((item) => item.id === normalizedRequestedFocus)
      ? normalizedRequestedFocus
    : "all";
  const activeFocusLabel =
    projectFocusOptions.find((item) => item.id === activeFocus)?.label ||
    projectFocusOptions[0].label;
  const matchesActiveFocus = (project) =>
    activeFocus === "all" || project.focusAreas?.includes(activeFocus);

  const filteredProjects = projectData.filter(matchesActiveFocus);
  const featuredProjects = filteredProjects.filter((project) => project.featured);
  const additionalProjects = filteredProjects.filter((project) => !project.featured);
  const visibleProjects = [...featuredProjects, ...additionalProjects];
  const detailSearch = searchParams.toString() ? `?${searchParams.toString()}` : "";
  const briefSearch =
    activeFocus === "all" ? "" : `?focus=${encodeURIComponent(activeFocus)}`;

  useEffect(() => {
    if (requestedFocus === activeFocus) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);

    if (activeFocus === "all") {
      nextParams.delete("focus");
    } else {
      nextParams.set("focus", activeFocus);
    }

    setSearchParams(nextParams, { replace: true });
  }, [activeFocus, requestedFocus, searchParams, setSearchParams]);

  const handleFocusChange = (nextFocus) => {
    const nextParams = new URLSearchParams(searchParams);

    if (nextFocus === "all") {
      nextParams.delete("focus");
    } else {
      nextParams.set("focus", nextFocus);
    }

    setSearchParams(nextParams, { replace: true });
  };

  const getProjectDetailTo = (slug) => ({
    pathname: `/projects/${slug}`,
    search: detailSearch,
  });

  usePageMeta(
    "Projects | Om Jaiswal",
    "Selected projects by Om Jaiswal covering healthcare web apps, websites, product workflows, and supporting integrations."
  );

  return (
    <Container fluid className="project-section">
      <Particle />
      <Container className="section-shell">
        <h1 className="project-heading">
          Selected <strong className="purple">Projects</strong>
        </h1>
        <p className="section-supporting-copy projects-support-copy">
          All matching projects are shown in one list. Open any detail page for the full workflow story.
        </p>

        <div className="projects-filter-shell">
          <div className="projects-filter-header">
            <div>
              <p className="project-mini-heading">Browse by relevance</p>
              <p className="projects-filter-copy">
                Pick the closest project type so the most relevant proof shows first.
              </p>
            </div>
            <p className="projects-filter-status">
              Showing {filteredProjects.length} project{filteredProjects.length === 1 ? "" : "s"}
              {activeFocus === "all" ? "." : ` for ${activeFocusLabel.toLowerCase()}.`}
            </p>
          </div>

          <div className="projects-filter-list" aria-label="Project filters">
            {projectFocusOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`projects-filter-button${activeFocus === option.id ? " is-active" : ""}`}
                aria-pressed={activeFocus === option.id}
                onClick={() => handleFocusChange(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="projects-evaluation-grid">
          {evaluationGuides.map((item) => (
            <article className="projects-evaluation-card" key={item.label}>
              <span className="projects-evaluation-icon" aria-hidden="true">
                <item.Icon />
              </span>
              <div>
                <span className="project-mini-heading">{item.label}</span>
                <p>{item.text}</p>
              </div>
            </article>
          ))}
        </div>

        {visibleProjects.length > 0 ? (
          <Row className="card-grid-row g-4 projects-card-grid">
            {visibleProjects.map((project) => (
              <Col md={6} lg={4} key={project.title}>
                <ProjectCard
                  {...project}
                  detailTo={getProjectDetailTo(project.slug)}
                />
              </Col>
            ))}
          </Row>
        ) : null}

        {filteredProjects.length === 0 ? (
          <div className="projects-empty-state">
            <p className="section-kicker">No direct matches yet</p>
            <h2 className="projects-subtitle">Reset the filter to review the full proof set.</h2>
            <p>
              This focus is not represented in the current portfolio slice yet,
              but the broader project range may still be relevant.
            </p>
            <Button type="button" onClick={() => handleFocusChange("all")}>
              Show all selected work
            </Button>
          </div>
        ) : null}

        <div className="projects-cta-banner">
          <div>
            <p className="section-kicker">Project fit</p>
            <h2>Need a similar build with cleaner UX and stronger execution?</h2>
            <p>
              Send a brief with the website, web app, or product workflow you
              want to improve. I will respond with a practical next step.
            </p>
          </div>

          <div className="projects-cta-actions">
            <Button
              as={Link}
              to={{
                pathname: "/",
                search: briefSearch,
                hash: "#project-brief",
              }}
            >
              Start a Structured Brief
            </Button>
            <Button as={Link} to="/experience" variant="outline-light">
              Review Experience
            </Button>
          </div>
        </div>
      </Container>
    </Container>
  );
}

export default Projects;
